/*
 * Product hotspots over a hero photograph.
 *
 * Storefront: open/close a card per marker, Ajax quick add to cart, and placement that
 * respects the merchant's chosen side but flips when that side would run off screen.
 *
 * Theme editor: markers are draggable with the mouse and nudgeable with the arrow keys.
 * Shopify gives a section's own JavaScript no API for writing block settings back from
 * inside the preview iframe, so the drag updates the marker live and hands back the exact
 * X/Y - with a copy button - to paste into the block's sliders. The sliders remain the
 * saved source of truth.
 */
(() => {
  if (customElements.get('product-hotspot-hero')) return;

  const VERTICAL_PLACEMENTS = ['below', 'above'];
  const OPPOSITE = { below: 'above', above: 'below', right: 'left', left: 'right' };
  // The live homepage's two grip markers were positioned before coordinates were projected
  // through the source image. Shopify can continue serving a saved storefront snapshot
  // after the underlying theme settings are updated, so migrate only those exact legacy
  // desktop pairs at render time. Once the new values reach the storefront they no longer
  // match and this becomes a no-op. Mobile has its own composition and is never migrated.
  const LEGACY_DESKTOP_POSITIONS = {
    hotspot_p9QLTx: { from: [67, 55], to: [63, 54] },
    hotspot_zpAfph: { from: [33, 60], to: [37, 55] }
  };

  class ProductHotspotHero extends HTMLElement {
    connectedCallback() {
      if (this.dataset.hotspotInitialized === 'true') return;
      this.dataset.hotspotInitialized = 'true';

      this.isDesignMode = this.dataset.designMode === 'true';
      this.mobileBreakpoint = Number(this.dataset.mobileBreakpoint) || 749;
      this.hotspotButtons = Array.from(this.querySelectorAll('[data-hotspot-button]'));
      this.quickAddButtons = Array.from(this.querySelectorAll('[data-quick-add]'));
      this.frame = this.querySelector('[data-hotspot-frame]');
      this.image = this.querySelector('[data-hotspot-image]');
      // Markers live in this box, and when the background is frozen it IS the photo's box.
      // Everything that converts between pixels and percentages measures against it, so the
      // same code works frozen or not.
      this.layer = this.querySelector('[data-hotspot-layer]');
      this.frozen = this.dataset.frozen === 'true';
      this.frozenActive = false;
      this.frozenEngaged = false;
      this.imageProjection = null;
      this.geometryFrame = null;
      this.resizeObserver = null;
      this.editorStatus = this.querySelector('[data-editor-status]');
      this.editorStatusText = this.querySelector('[data-editor-status-text]');
      this.editorCopy = this.querySelector('[data-editor-copy]');
      this.dragState = null;
      this.lastEditorValues = null;
      this.suppressClickUntil = 0;
      this.editorStatusTimer = null;
      this.inspectorActive = false;

      this.onHotspotClick = this.onHotspotClick.bind(this);
      this.onCloseClick = this.onCloseClick.bind(this);
      this.onQuickAddClick = this.onQuickAddClick.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
      this.onViewportChange = this.onViewportChange.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onBlockDeselect = this.onBlockDeselect.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onMarkerKeydown = this.onMarkerKeydown.bind(this);
      this.onCopyClick = this.onCopyClick.bind(this);
      this.onInspectorActivate = this.onInspectorActivate.bind(this);
      this.onInspectorDeactivate = this.onInspectorDeactivate.bind(this);
      this.onImageLoad = this.onImageLoad.bind(this);

      this.hotspotButtons.forEach((button) => {
        button.addEventListener('click', this.onHotspotClick);
        if (this.isDesignMode) {
          button.addEventListener('pointerdown', this.onPointerDown);
          button.addEventListener('keydown', this.onMarkerKeydown);
        }
      });

      this.querySelectorAll('[data-hotspot-close]').forEach((button) => {
        button.addEventListener('click', this.onCloseClick);
      });

      this.quickAddButtons.forEach((button) => {
        button.addEventListener('click', this.onQuickAddClick);
      });

      this.editorCopy?.addEventListener('click', this.onCopyClick);

      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onDocumentKeydown);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
      document.addEventListener('shopify:inspector:activate', this.onInspectorActivate);
      document.addEventListener('shopify:inspector:deactivate', this.onInspectorDeactivate);
      window.addEventListener('resize', this.onViewportChange);
      window.addEventListener('scroll', this.onViewportChange, { passive: true });
      window.visualViewport?.addEventListener('resize', this.onViewportChange);
      this.image?.addEventListener('load', this.onImageLoad);

      if ('ResizeObserver' in window && this.frame) {
        this.resizeObserver = new ResizeObserver(this.onViewportChange);
        this.resizeObserver.observe(this.frame);
      }

      this.syncGeometry();
    }

    disconnectedCallback() {
      this.hotspotButtons?.forEach((button) => {
        button.removeEventListener('click', this.onHotspotClick);
        button.removeEventListener('pointerdown', this.onPointerDown);
        button.removeEventListener('keydown', this.onMarkerKeydown);
      });

      this.querySelectorAll('[data-hotspot-close]').forEach((button) => {
        button.removeEventListener('click', this.onCloseClick);
      });

      this.quickAddButtons?.forEach((button) => {
        button.removeEventListener('click', this.onQuickAddClick);
      });

      this.editorCopy?.removeEventListener('click', this.onCopyClick);

      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onDocumentKeydown);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      document.removeEventListener('shopify:inspector:activate', this.onInspectorActivate);
      document.removeEventListener('shopify:inspector:deactivate', this.onInspectorDeactivate);
      window.removeEventListener('resize', this.onViewportChange);
      window.removeEventListener('scroll', this.onViewportChange);
      window.visualViewport?.removeEventListener('resize', this.onViewportChange);
      this.image?.removeEventListener('load', this.onImageLoad);
      this.resizeObserver?.disconnect();
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('pointercancel', this.onPointerUp);

      if (this.editorStatusTimer) window.clearTimeout(this.editorStatusTimer);
      if (this.geometryFrame) window.cancelAnimationFrame(this.geometryFrame);
    }

    /*
     * Frozen background: the photo and the marker layer are both pinned to the viewport, so
     * their box has to be recomputed as the page moves. Five custom properties - the box, and
     * the top/bottom insets that clip the layer down to the part of the photo the band is
     * currently revealing.
     *
     * Throttled to one write per frame. Scroll fires far more often than that.
     */
    syncFrozenGeometry() {
      if (!this.frozen || !this.frame) return;

      const active = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (active !== this.frozenActive) {
        this.frozenActive = active;
        if (!active) {
          this.frozenEngaged = false;
          this.classList.remove('is-geometry-ready');
          [
            '--phh-frame-left',
            '--phh-frame-width',
            '--phh-photo-top',
            '--phh-photo-height',
            '--phh-band-top',
            '--phh-band-bottom'
          ].forEach((name) => this.style.removeProperty(name));
        }
      }

      if (!active) return;

      const f = this.frame.getBoundingClientRect();
      // The mobile artwork and its five marker coordinates were authored against the mobile
      // section's aspect ratio. Freeze that exact box instead of stretching it to the taller
      // phone viewport; desktop keeps its full-viewport treatment.
      const photoHeight = this.isMobileLayout() ? f.height : Math.max(window.innerHeight, f.height);
      // While the section is entering below the header, keep the photo aligned to the
      // section just as it is in the editor. Freeze it only once the section reaches the
      // viewport edge; pinning it to 0 from page load starts the storefront part-way down
      // the source image and makes the editor's carefully placed composition look shifted.
      const photoTop = Math.max(0, f.top);
      const visibleTop = Math.max(0, f.top);
      const visibleBottom = Math.min(window.innerHeight, f.bottom);

      this.style.setProperty('--phh-frame-left', `${Math.round(f.left)}px`);
      this.style.setProperty('--phh-frame-width', `${Math.round(f.width)}px`);
      this.style.setProperty('--phh-photo-top', `${Math.round(photoTop)}px`);
      this.style.setProperty('--phh-photo-height', `${Math.round(photoHeight)}px`);
      this.style.setProperty('--phh-band-top', `${Math.round(Math.max(0, visibleTop - photoTop))}px`);
      this.style.setProperty(
        '--phh-band-bottom',
        `${Math.round(Math.max(0, photoTop + photoHeight - visibleBottom))}px`
      );
      // Keep the first paint in the normal document box. Switching an above-the-fold
      // image and its controls from absolute to fixed during startup moves the whole hero
      // and is measured as layout shift. Engage the frozen treatment only after the
      // visitor actually starts scrolling; scroll-driven movement is then expected and
      // the initial rendering remains stable.
      if (this.frozenEngaged || window.scrollY > 0) {
        this.frozenEngaged = true;
        this.classList.add('is-geometry-ready');
      }
    }

    /*
     * A marker is authored against the ORIGINAL IMAGE, not against the section box. With
     * object-fit: cover those are different coordinate systems whenever their aspect ratios
     * differ: the image is scaled beyond one edge and cropped according to object-position.
     * Projecting source-image percentages through that exact transform keeps the marker on
     * the same rifle part at every viewport width and browser zoom.
     */
    syncImageProjection() {
      if (!this.layer) return;

      const layerRect = this.layer.getBoundingClientRect();
      if (!layerRect.width || !layerRect.height) return;

      const mobile = this.isMobileLayout();
      const useMobileImage = mobile && this.dataset.mobileImage === 'true';
      let sourceWidth = Number(useMobileImage ? this.dataset.mobileImageWidth : this.dataset.desktopImageWidth);
      let sourceHeight = Number(useMobileImage ? this.dataset.mobileImageHeight : this.dataset.desktopImageHeight);

      // The placeholder has no source-image geometry. Preserve the old box-relative fallback
      // so a newly inserted section is still editable before the merchant chooses a photo.
      if (!this.image || sourceWidth <= 1 || sourceHeight <= 1) {
        sourceWidth = layerRect.width;
        sourceHeight = layerRect.height;
      }

      const scale = Math.max(layerRect.width / sourceWidth, layerRect.height / sourceHeight);
      const width = sourceWidth * scale;
      const height = sourceHeight * scale;
      const position = this.parseImagePosition(
        useMobileImage ? this.dataset.mobileImagePosition : this.dataset.desktopImagePosition
      );
      const left = (layerRect.width - width) * (position.x / 100);
      const top = (layerRect.height - height) * (position.y / 100);

      this.imageProjection = { layerRect, left, top, width, height, mobile };
      this.style.setProperty('--phh-rendered-image-left', `${left.toFixed(2)}px`);
      this.style.setProperty('--phh-rendered-image-top', `${top.toFixed(2)}px`);
      this.style.setProperty('--phh-rendered-image-width', `${width.toFixed(2)}px`);
      this.style.setProperty('--phh-rendered-image-height', `${height.toFixed(2)}px`);

      this.querySelectorAll('[data-hotspot-block-id]').forEach((hotspot) => {
        this.projectHotspot(hotspot, this.imageProjection);
      });
    }

    parseImagePosition(value) {
      const percentages = String(value || '50% 50%').match(/-?[\d.]+(?=%)/g) || [];
      return {
        x: this.clamp(Number(percentages[0] ?? 50), 0, 100),
        y: this.clamp(Number(percentages[1] ?? percentages[0] ?? 50), 0, 100)
      };
    }

    isMobileLayout() {
      return window.matchMedia(`(max-width: ${this.mobileBreakpoint}px)`).matches;
    }

    projectHotspot(hotspot, projection) {
      if (!projection) return;

      const useMobile = projection.mobile && hotspot.dataset.mobileOverride === 'true';
      let x = Number(useMobile ? hotspot.dataset.mobileX : hotspot.dataset.desktopX);
      let y = Number(useMobile ? hotspot.dataset.mobileY : hotspot.dataset.desktopY);
      if (!Number.isFinite(x) || !Number.isFinite(y)) return;

      const legacyPosition = !projection.mobile
        ? LEGACY_DESKTOP_POSITIONS[hotspot.dataset.hotspotBlockId]
        : null;
      if (legacyPosition && x === legacyPosition.from[0] && y === legacyPosition.from[1]) {
        [x, y] = legacyPosition.to;
      }

      const renderedX = projection.left + (projection.width * x) / 100;
      const renderedY = projection.top + (projection.height * y) / 100;
      hotspot.style.setProperty('--hotspot-render-x', `${renderedX.toFixed(2)}px`);
      hotspot.style.setProperty('--hotspot-render-y', `${renderedY.toFixed(2)}px`);

      // A source point can legitimately be outside the visible crop. Hiding it is safer
      // than letting it float over an unrelated part of the photograph.
      const croppedOut =
        renderedX < 0 || renderedX > projection.layerRect.width || renderedY < 0 || renderedY > projection.layerRect.height;
      hotspot.classList.toggle('is-cropped-out', croppedOut);

      const button = hotspot.querySelector('[data-hotspot-button]');
      if (croppedOut && button?.getAttribute('aria-expanded') === 'true') this.closeHotspot(button);
    }

    syncGeometry() {
      this.syncFrozenGeometry();
      this.syncImageProjection();
    }

    onImageLoad() {
      this.onViewportChange();
    }

    /*
     * The vertical room a card may occupy. Normally the viewport; when frozen, only the slice
     * of it the band is showing, because the layer is clipped to exactly that and a card
     * placed outside it would be sliced in half.
     */
    getVerticalBounds() {
      if (!this.frozenActive || !this.frame) {
        return { top: 0, bottom: window.innerHeight };
      }

      const f = this.frame.getBoundingClientRect();
      return { top: Math.max(0, f.top), bottom: Math.min(window.innerHeight, f.bottom) };
    }

    isMarkerInBand(button) {
      if (!this.frame) return true;

      const f = this.frame.getBoundingClientRect();
      const m = button.getBoundingClientRect();
      return m.bottom > f.top && m.top < f.bottom;
    }

    /* ---- Opening and closing ---------------------------------------------------------- */

    onHotspotClick(event) {
      event.preventDefault();
      event.stopPropagation();

      // A drag that ends on the marker also fires a click. Swallow it.
      if (performance.now() < this.suppressClickUntil) return;

      const button = event.currentTarget;
      if (button.getAttribute('aria-expanded') === 'true') {
        this.closeHotspot(button);
      } else {
        this.openHotspot(button);
      }
    }

    onCloseClick(event) {
      event.preventDefault();
      event.stopPropagation();

      const card = event.currentTarget.closest('[data-hotspot-card]');
      const button = card ? this.querySelector(`[aria-controls="${CSS.escape(card.id)}"]`) : null;

      if (button) this.closeHotspot(button, true);
    }

    onDocumentClick(event) {
      if (!this.contains(event.target)) {
        this.closeAllHotspots();
        return;
      }

      const clickedInsideCard = event.target.closest('[data-hotspot-card]');
      const clickedButton = event.target.closest('[data-hotspot-button]');

      if (!clickedInsideCard && !clickedButton) this.closeAllHotspots();
    }

    onDocumentKeydown(event) {
      if (event.key !== 'Escape') return;

      if (this.dragState) this.finishDrag(false);

      const openButton = this.getOpenButton();
      if (openButton) this.closeHotspot(openButton, true);
    }

    onViewportChange() {
      if (this.geometryFrame) return;

      this.geometryFrame = window.requestAnimationFrame(() => {
        this.geometryFrame = null;
        this.syncGeometry();

        const openButton = this.getOpenButton();
        if (!openButton) return;

        // Frozen: the band scrolls past the photo, so a marker eventually leaves the window
        // and is clipped away. Its card would otherwise linger as a sliver with nothing
        // attached to it, so the card goes when the marker does.
        if (this.frozenActive && !this.isMarkerInBand(openButton)) {
          this.closeHotspot(openButton);
          return;
        }

        this.positionCard(openButton);
      });
    }

    onBlockSelect(event) {
      const block = event.target.closest?.('[data-hotspot-block-id]');
      if (!block || !this.contains(block)) return;

      const button = block.querySelector('[data-hotspot-button]');
      if (button) this.openHotspot(button);
    }

    onBlockDeselect(event) {
      const block = event.target.closest?.('[data-hotspot-block-id]');
      if (!block || !this.contains(block)) return;

      const button = block.querySelector('[data-hotspot-button]');
      if (button) this.closeHotspot(button);
    }

    onInspectorActivate() {
      this.inspectorActive = true;
    }

    onInspectorDeactivate() {
      this.inspectorActive = false;
    }

    getOpenButton() {
      return this.querySelector('[data-hotspot-button][aria-expanded="true"]');
    }

    getCard(button) {
      const cardId = button.getAttribute('aria-controls');
      return cardId ? this.querySelector(`#${CSS.escape(cardId)}`) : null;
    }

    openHotspot(button) {
      this.closeAllHotspots(button);

      const card = this.getCard(button);
      if (!card) return;

      // Re-opening a card that is still playing its close animation: abandon that close,
      // otherwise its pending handler would hide the card we are about to show.
      this.cancelPendingClose(card);

      button.setAttribute('aria-expanded', 'true');
      card.hidden = false;
      card.setAttribute('aria-hidden', 'false');
      const hotspot = button.closest('[data-hotspot-block-id]');
      hotspot?.classList.add('is-open');

      requestAnimationFrame(() => this.positionCard(button));

      // The thumbnail is lazy and the card starts hidden, so the first open measures a card
      // that is still short. Re-place it once the image has actually arrived.
      const image = card.querySelector('.product-hotspot-hero__card-image');
      if (image && !image.complete) {
        image.addEventListener('load', () => this.positionCard(button), { once: true });
      }
    }

    /*
     * The card plays its close animation before it disappears. Everything that is a state
     * change rather than a visual one - aria, the marker's plus - happens immediately, so
     * assistive technology and the marker never lag behind the click.
     */
    closeHotspot(button, restoreFocus = false) {
      const card = this.getCard(button);
      if (!card || card.hidden) return;

      button.setAttribute('aria-expanded', 'false');
      card.setAttribute('aria-hidden', 'true');
      const hotspot = button.closest('[data-hotspot-block-id]');
      hotspot?.classList.remove('is-open');

      if (restoreFocus) button.focus();

      this.cancelPendingClose(card);
      card.classList.add('is-closing');

      const finish = () => {
        this.cancelPendingClose(card);
        card.hidden = true;
        card.style.removeProperty('--phh-card-shift-x');
        card.style.removeProperty('--phh-card-shift-y');
        card.style.removeProperty('--phh-card-mobile-y');
        card.dataset.placement = card.dataset.placementPreferred || 'below';
      };

      const onEnd = (event) => {
        if (event.target !== card) return;
        finish();
      };

      card.addEventListener('animationend', onEnd);
      // Belt and braces: a card in a background tab, or one whose animation never starts,
      // must still end up hidden.
      const timer = window.setTimeout(finish, 400);

      card.phhCloseCleanup = () => {
        window.clearTimeout(timer);
        card.removeEventListener('animationend', onEnd);
        card.classList.remove('is-closing');
        card.phhCloseCleanup = null;
      };
    }

    cancelPendingClose(card) {
      card.phhCloseCleanup?.();
    }

    closeAllHotspots(exceptButton = null) {
      this.hotspotButtons.forEach((button) => {
        if (button !== exceptButton && button.getAttribute('aria-expanded') === 'true') {
          this.closeHotspot(button);
        }
      });
    }

    /* ---- Placement -------------------------------------------------------------------- */

    /*
     * Start from the placement the merchant chose. Flip to the opposite side only when the
     * chosen side genuinely has less room than its opposite, then slide the card along the
     * free axis so it stays inside both the section and the viewport.
     */
    /*
     * Sizes come from offsetWidth/offsetHeight and positions are derived from the marker,
     * never from the card's own getBoundingClientRect. That rect is the VISUAL box, so while
     * the open animation is mid-flight it reports the card scaled to 0.9 and offset - and
     * clamping against those numbers put a phone-width card off screen.
     */
    positionCard(button) {
      const card = this.getCard(button);
      if (!card || card.hidden) return;

      const preferred = card.dataset.placementPreferred || 'below';
      const isMobile = this.isMobileLayout();

      // On the mobile composition every card uses one predictable dock at the bottom of the
      // visible image. Keeping a card attached directly to a marker covers the other rifle
      // parts at 320-390px, making those hotspots appear to vanish.
      let placement = isMobile ? 'below' : preferred;

      card.style.setProperty('--phh-card-shift-x', '0px');
      card.style.setProperty('--phh-card-shift-y', '0px');
      card.style.setProperty('--phh-card-mobile-y', '0px');
      card.dataset.placement = placement;

      const triggerRect = button.getBoundingClientRect();
      const cardWidth = card.offsetWidth;
      const cardHeight = card.offsetHeight;
      const bounds = this.getVerticalBounds();
      const gap = 12;
      const padding = 8;

      if (isMobile) {
        const frameRect = this.frame?.getBoundingClientRect() || this.getBoundingClientRect();
        const visibleTop = Math.max(padding, frameRect.top + gap);
        const visibleBottom = Math.min(window.innerHeight - padding, frameRect.bottom - gap);
        const desiredTop = Math.max(visibleTop, visibleBottom - cardHeight);
        const naturalTop = triggerRect.bottom + gap;

        card.style.setProperty('--phh-card-mobile-y', `${Math.round(desiredTop - naturalTop)}px`);
        card.style.setProperty('--phh-card-shift-x', `${this.horizontalShift(triggerRect, cardWidth, padding)}px`);
        return;
      }

      if (VERTICAL_PLACEMENTS.includes(placement)) {
        const needed = cardHeight + gap;
        const spaceBelow = bounds.bottom - triggerRect.bottom;
        const spaceAbove = triggerRect.top - bounds.top;
        const chosenSpace = placement === 'below' ? spaceBelow : spaceAbove;
        const otherSpace = placement === 'below' ? spaceAbove : spaceBelow;

        if (chosenSpace < needed && otherSpace > chosenSpace) {
          placement = OPPOSITE[placement];
          card.dataset.placement = placement;
        }

        card.style.setProperty('--phh-card-shift-x', `${this.horizontalShift(triggerRect, cardWidth, padding)}px`);
        return;
      }

      const needed = cardWidth + gap;
      const spaceRight = window.innerWidth - triggerRect.right;
      const spaceLeft = triggerRect.left;
      const chosenSpace = placement === 'right' ? spaceRight : spaceLeft;
      const otherSpace = placement === 'right' ? spaceLeft : spaceRight;

      if (chosenSpace < needed && otherSpace > chosenSpace) {
        placement = OPPOSITE[placement];
        card.dataset.placement = placement;
      }

      card.style.setProperty(
        '--phh-card-shift-y',
        `${this.verticalShift(triggerRect, cardHeight, padding, bounds)}px`
      );
    }

    // How far the card must slide horizontally to sit inside the section and the viewport.
    horizontalShift(triggerRect, cardWidth, padding) {
      const componentRect = this.getBoundingClientRect();

      let leftBoundary = Math.max(padding, componentRect.left + padding);
      let rightBoundary = Math.min(window.innerWidth - padding, componentRect.right - padding);

      if (rightBoundary - leftBoundary < cardWidth) {
        leftBoundary = padding;
        rightBoundary = window.innerWidth - padding;
      }

      const desiredLeft = triggerRect.left + triggerRect.width / 2 - cardWidth / 2;
      const maxLeft = Math.max(leftBoundary, rightBoundary - cardWidth);
      const clampedLeft = Math.min(Math.max(desiredLeft, leftBoundary), maxLeft);

      return Math.round(clampedLeft - desiredLeft);
    }

    // Same idea on the vertical axis, for the side placements. A side card is centred on the
    // marker, so its untransformed top follows from the marker rather than being measured.
    verticalShift(triggerRect, cardHeight, padding, bounds) {
      const top = triggerRect.top + triggerRect.height / 2 - cardHeight / 2;

      const overflowTop = bounds.top + padding - top;
      if (overflowTop > 0) return Math.round(overflowTop);

      const overflowBottom = top + cardHeight - (bounds.bottom - padding);
      if (overflowBottom > 0) return -Math.round(overflowBottom);

      return 0;
    }

    /* ---- Quick add -------------------------------------------------------------------- */

    async onQuickAddClick(event) {
      event.preventDefault();
      event.stopPropagation();

      const button = event.currentTarget;
      if (button.disabled || button.classList.contains('is-loading')) return;

      const variantId = Number(button.dataset.variantId);
      if (!variantId) return;

      const defaultLabel = button.dataset.defaultLabel || 'הוספה לסל';
      const loadingLabel = button.dataset.loadingLabel || 'מוסיף...';
      const successLabel = button.dataset.successLabel || 'נוסף לסל';
      const errorLabel = button.dataset.errorLabel || 'נסו שוב';
      const status = button.closest('[data-hotspot-card]')?.querySelector('[data-quick-add-status]');

      button.disabled = true;
      button.classList.add('is-loading');
      button.classList.remove('is-added');
      button.textContent = loadingLabel;
      if (status) status.textContent = '';

      try {
        const response = await fetch(this.dataset.cartAddUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
        });

        const result = await response.json();
        if (!response.ok) {
          throw new Error(result.description || result.message || 'לא ניתן להוסיף את המוצר לסל.');
        }

        const cart = await this.fetchCart();
        this.updateVisibleCartCounts(cart?.item_count);
        this.publishCartEvents(cart, result, variantId);

        button.classList.remove('is-loading');
        button.classList.add('is-added');
        button.textContent = successLabel;
        if (status) status.textContent = 'המוצר נוסף לסל.';

        if (this.dataset.afterAdd === 'cart') {
          window.location.assign(this.dataset.cartUrl);
          return;
        }

        window.setTimeout(() => {
          if (!button.isConnected) return;
          button.disabled = false;
          button.classList.remove('is-added');
          button.textContent = defaultLabel;
          if (status) status.textContent = '';
        }, 1800);
      } catch (error) {
        button.classList.remove('is-loading');
        button.textContent = errorLabel;
        if (status) status.textContent = error.message || 'אירעה שגיאה. נסו שוב.';

        window.setTimeout(() => {
          if (!button.isConnected) return;
          button.disabled = false;
          button.textContent = defaultLabel;
        }, 2200);
      }
    }

    async fetchCart() {
      try {
        const response = await fetch(this.dataset.cartJsonUrl, { headers: { Accept: 'application/json' } });
        if (!response.ok) return null;
        return await response.json();
      } catch (_error) {
        return null;
      }
    }

    updateVisibleCartCounts(itemCount) {
      if (!Number.isFinite(itemCount)) return;

      const selectors = [
        '[data-cart-count]',
        '[data-cart-count-bubble] [aria-hidden="true"]',
        '.cart-count-bubble span[aria-hidden="true"]'
      ];

      const elements = new Set();
      selectors.forEach((selector) => {
        document.querySelectorAll(selector).forEach((element) => elements.add(element));
      });

      elements.forEach((element) => {
        element.textContent = String(itemCount);
        element.hidden = itemCount === 0;
      });
    }

    publishCartEvents(cart, lineItemResponse, variantId) {
      const detail = {
        source: 'product-hotspot-hero',
        cart,
        lineItemResponse,
        productVariantId: variantId
      };

      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail }));
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail }));

      if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
          source: 'product-hotspot-hero',
          productVariantId: variantId,
          cartData: cart || lineItemResponse
        });
      }
    }

    /* ---- Dragging, theme editor only -------------------------------------------------- */

    onPointerDown(event) {
      if (!this.isDesignMode || this.inspectorActive || event.button !== 0 || !this.layer) return;

      const button = event.currentTarget;
      const hotspot = button.closest('[data-hotspot-block-id]');
      if (!hotspot) return;

      this.syncImageProjection();
      const projection = this.imageProjection;
      if (!projection) return;

      this.dragState = {
        pointerId: event.pointerId,
        button,
        hotspot,
        startX: event.clientX,
        startY: event.clientY,
        moved: false,
        // The saved value is a coordinate on the source image. Keep the complete cover/crop
        // projection from pointer-down so the inverse transform stays stable through drag.
        projection,
        mobile: this.isMobileLayout(),
        mobileOverride: hotspot.dataset.mobileOverride === 'true'
      };

      button.setPointerCapture?.(event.pointerId);
      window.addEventListener('pointermove', this.onPointerMove);
      window.addEventListener('pointerup', this.onPointerUp);
      window.addEventListener('pointercancel', this.onPointerUp);
    }

    onPointerMove(event) {
      const state = this.dragState;
      if (!state || event.pointerId !== state.pointerId) return;

      // A few pixels of slop, so a click on the marker is still a click.
      const distance = Math.hypot(event.clientX - state.startX, event.clientY - state.startY);
      if (!state.moved && distance < 5) return;

      if (!state.moved) {
        state.moved = true;
        state.hotspot.classList.add('is-dragging');
        this.closeAllHotspots();
      }

      event.preventDefault();

      const x = this.clamp(
        ((event.clientX - state.projection.layerRect.left - state.projection.left) / state.projection.width) * 100,
        0,
        100
      );
      const y = this.clamp(
        ((event.clientY - state.projection.layerRect.top - state.projection.top) / state.projection.height) * 100,
        0,
        100
      );

      this.applyPosition(state.hotspot, Math.round(x), Math.round(y), state.mobile && state.mobileOverride);
      state.lastX = Math.round(x);
      state.lastY = Math.round(y);
      state.usesMobilePosition = state.mobile && state.mobileOverride;
    }

    onPointerUp(event) {
      const state = this.dragState;
      if (!state || event.pointerId !== state.pointerId) return;
      this.finishDrag(true);
    }

    finishDrag(showFinalStatus) {
      const state = this.dragState;
      if (!state) return;

      state.button.releasePointerCapture?.(state.pointerId);
      state.hotspot.classList.remove('is-dragging');
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('pointercancel', this.onPointerUp);

      if (state.moved) {
        this.suppressClickUntil = performance.now() + 400;
        if (showFinalStatus) {
          this.showEditorStatus(state.lastX, state.lastY, state.usesMobilePosition, true);
        }
      }

      this.dragState = null;
    }

    // Arrow keys nudge the focused marker; Shift takes 5% steps.
    onMarkerKeydown(event) {
      if (!this.isDesignMode) return;

      const deltas = {
        ArrowRight: [1, 0],
        ArrowLeft: [-1, 0],
        ArrowUp: [0, -1],
        ArrowDown: [0, 1]
      };
      const delta = deltas[event.key];
      if (!delta) return;

      const hotspot = event.currentTarget.closest('[data-hotspot-block-id]');
      if (!hotspot) return;

      event.preventDefault();

      const step = event.shiftKey ? 5 : 1;
      const useMobile =
        this.isMobileLayout() && hotspot.dataset.mobileOverride === 'true';
      const currentX = Number(useMobile ? hotspot.dataset.mobileX : hotspot.dataset.desktopX) || 0;
      const currentY = Number(useMobile ? hotspot.dataset.mobileY : hotspot.dataset.desktopY) || 0;
      const x = this.clamp(currentX + delta[0] * step, 0, 100);
      const y = this.clamp(currentY + delta[1] * step, 0, 100);

      this.applyPosition(hotspot, x, y, useMobile);
      this.showEditorStatus(x, y, useMobile, true);
    }

    applyPosition(hotspot, x, y, useMobile) {
      if (useMobile) {
        hotspot.style.setProperty('--hotspot-mobile-x', `${x}%`);
        hotspot.style.setProperty('--hotspot-mobile-y', `${y}%`);
        hotspot.dataset.mobileX = String(x);
        hotspot.dataset.mobileY = String(y);
      } else {
        hotspot.style.setProperty('--hotspot-x', `${x}%`);
        hotspot.style.setProperty('--hotspot-y', `${y}%`);
        hotspot.dataset.desktopX = String(x);
        hotspot.dataset.desktopY = String(y);
      }

      this.projectHotspot(hotspot, this.imageProjection);

      if (this.dragState) return;
      this.lastEditorValues = { x, y };
    }

    showEditorStatus(x, y, mobile, final) {
      if (!this.editorStatus || x === undefined || y === undefined) return;

      this.lastEditorValues = { x, y };

      // Editor chrome, so English - only the merchant ever sees it.
      const mode = mobile ? 'Mobile' : 'Desktop';
      this.editorStatus.hidden = false;
      if (this.editorStatusText) {
        this.editorStatusText.textContent = final
          ? `${mode}: X ${x}% · Y ${y}% - enter these in the hotspot's sliders to save`
          : `${mode}: X ${x}% · Y ${y}%`;
      }
      if (this.editorCopy) {
        this.editorCopy.hidden = !final;
        this.editorCopy.textContent = 'Copy values';
      }

      if (this.editorStatusTimer) window.clearTimeout(this.editorStatusTimer);
      if (final) {
        this.editorStatusTimer = window.setTimeout(() => {
          if (this.editorStatus) this.editorStatus.hidden = true;
        }, 9000);
      }
    }

    async onCopyClick(event) {
      event.preventDefault();
      if (!this.lastEditorValues) return;

      const text = `X ${this.lastEditorValues.x} / Y ${this.lastEditorValues.y}`;

      try {
        await navigator.clipboard.writeText(text);
        event.currentTarget.textContent = 'Copied';
      } catch (_error) {
        event.currentTarget.textContent = text;
      }
    }

    clamp(value, min, max) {
      return Math.min(Math.max(value, min), max);
    }
  }

  customElements.define('product-hotspot-hero', ProductHotspotHero);
})();
